/**
 * editor2/hooks/useAutoChangeMemo.ts
 * 自動変更メモ生成ロジック
 * ReservationEditorPanel.tsx 行14-187 から正確に抽出
 */
import { useCallback } from 'react';
import {
  AUTO_MEMO_FIELD_CONFIGS,
  ACTION_LABELS,
  ACTION_PRIORITY,
  COMM_TOOL_NAMES,
} from '../constants';
import type { StatusOption } from '../types';

export interface AutoMemoResult {
  actionType: string;
  changeLines: string[];
}

/**
 * originalJson(文字列) と current(オブジェクト) を比較し、
 * 変更があった場合にアクションタイプと変更行を返す。
 * 変更がない場合は null を返す。
 */
export function generateAutoChangeMemo(
  originalJsonStr: string | null,
  current: Record<string, any> | null,
  statusOpts?: StatusOption[],
): AutoMemoResult | null {
  if (!originalJsonStr || !current) return null;

  let original: Record<string, any>;
  try {
    original = JSON.parse(originalJsonStr);
  } catch {
    return null;
  }

  const formatValue = (config: any, val: any): string => {
    if (config.formatByStatus && statusOpts) {
      const s = statusOpts.find(
        (st) => String(st.id) === String(val) || st.code === val,
      );
      return s?.label || val || 'なし';
    }
    if (config.formatDate) {
      return val ? String(val).replace('T', ' ').slice(0, 16) : '未設定';
    }
    if (config.formatPrice) {
      return val != null && val !== '' ? `\u00a5${Number(val).toLocaleString()}` : '\u00a50';
    }
    if (config.formatBool) {
      return val ? 'ON' : 'OFF';
    }
    if (config.formatCheck) {
      return val ? '\u2713' : '\u2717';
    }
    if (config.formatCommTools) {
      const arr = Array.isArray(val) ? val : [];
      return arr.length > 0 ? arr.map((id: number) => COMM_TOOL_NAMES[id] || id).join(', ') : 'なし';
    }
    return val != null && val !== '' ? String(val) : 'なし';
  };

  const changes: Array<{
    label: string;
    category: string;
    oldDisplay: string;
    newDisplay: string;
  }> = [];
  const categoriesFound = new Set<string>();

  for (const config of AUTO_MEMO_FIELD_CONFIGS) {
    // memo自体の変更は記録しない
    if ((config.field as string) === 'memo') continue;

    const oldVal = original[config.field];
    const newVal = current[config.field];

    // 正規化して比較（配列はJSON化）
    const oldNorm = Array.isArray(oldVal) ? JSON.stringify(oldVal) : String(oldVal ?? '');
    const newNorm = Array.isArray(newVal) ? JSON.stringify(newVal) : String(newVal ?? '');

    if (oldNorm !== newNorm) {
      changes.push({
        label: config.label,
        category: config.category,
        oldDisplay: formatValue(config, oldVal),
        newDisplay: formatValue(config, newVal),
      });
      categoriesFound.add(config.category);
    }
  }

  if (changes.length === 0) return null;

  // キャンセルステータスへの変更を検知
  const statusChange = changes.find((c) => c.label === 'ステータス');
  let isCancel = false;
  if (statusChange && statusOpts) {
    const newStatusId = current.status;
    const targetStatus = statusOpts.find(
      (s) => String(s.id) === String(newStatusId) || s.code === newStatusId,
    );
    if (targetStatus?.label?.includes('キャンセル')) {
      isCancel = true;
    }
  }

  // アクションタイプ決定
  let actionType = 'other';
  if (isCancel) {
    actionType = 'cancel';
  } else {
    for (const priority of ACTION_PRIORITY) {
      if (categoriesFound.has(priority)) {
        actionType = priority;
        break;
      }
    }
  }

  // 変更行を生成
  const changeLines = changes.map((c) => `${c.label}: ${c.oldDisplay} \u2192 ${c.newDisplay}`);

  return { actionType, changeLines };
}

/**
 * スタッフ名を取得するヘルパー
 */
export function getStaffName(): string {
  if ((window as any).bo_staff && (window as any).bo_staff.name) {
    return (window as any).bo_staff.name;
  }
  const savedStaff = localStorage.getItem('bo_staff');
  if (savedStaff) {
    try {
      const staff = JSON.parse(savedStaff);
      return staff.name || 'スタッフ未設定';
    } catch (_e) {
      /* ignore */
    }
  }
  return 'スタッフ未設定';
}

/**
 * 現在のタイムスタンプ文字列を生成
 */
export function formatTimestamp(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}`;
}

/**
 * 自動変更メモ結果をフォーマットされたメモエントリ文字列に変換
 */
export function formatAutoMemoEntry(result: AutoMemoResult, staffName?: string): string {
  const ts = formatTimestamp();
  const name = staffName || getStaffName();
  const header = `---${ACTION_LABELS[result.actionType]} ${name} ${ts}---`;
  const content = result.changeLines.join('\n');
  const footer = '---';
  return `${header}\n${content}\n${footer}`;
}

/**
 * React hook: useAutoChangeMemo
 * コンポーネントで使いやすい形でラップ
 */
export default function useAutoChangeMemo(statusOptions?: StatusOption[]) {
  const generate = useCallback(
    (originalJsonStr: string | null, current: Record<string, any> | null) => {
      return generateAutoChangeMemo(originalJsonStr, current, statusOptions);
    },
    [statusOptions],
  );

  const generateAndFormat = useCallback(
    (originalJsonStr: string | null, current: Record<string, any> | null): string | null => {
      const result = generateAutoChangeMemo(originalJsonStr, current, statusOptions);
      if (!result) return null;
      return formatAutoMemoEntry(result);
    },
    [statusOptions],
  );

  return {
    generateAutoChangeMemo: generate,
    generateAndFormat,
    formatAutoMemoEntry,
    getStaffName,
    formatTimestamp,
  };
}
